import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import SectionTag from './SectionTag'

const EASE = [0.16, 1, 0.3, 1]

const fade = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 1, ease: EASE },
}

export default function Philosophy() {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%'])

  return (
    <section id="philosophy" ref={ref} className="relative px-6 py-24 md:px-10 md:py-40" data-testid="philosophy-section">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-14 md:grid-cols-12 md:gap-8">
        <div className="md:col-span-6">
          <SectionTag index="01" label="Why we exist" />
          <motion.h2 {...fade} className="display-l mt-8 max-w-[12ch]" data-testid="philosophy-headline">
            Because wellness got <span className="relative inline-block text-[#5B7BA8]">complicated.
              <motion.svg
                viewBox="0 0 260 16"
                className="absolute -bottom-2.5 left-0 w-full"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                aria-hidden="true"
              >
                <motion.path
                  d="M4 12 C 70 4, 180 4, 256 9"
                  fill="none"
                  stroke="#B1C9E8"
                  strokeWidth="6"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 1.3, ease: EASE, delay: 0.5 }}
                />
              </motion.svg>
            </span>
          </motion.h2>
        </div>

        <div className="md:col-span-5 md:col-start-8 md:pt-24">
          <motion.p {...fade} className="text-lg leading-relaxed" data-testid="philosophy-copy-1">
            Endless claims. Complicated routines. Perfect habits that are impossible to keep.
          </motion.p>
          <motion.p {...fade} transition={{ duration: 1, ease: EASE, delay: 0.12 }} className="mt-6 text-lg leading-relaxed text-haze" data-testid="philosophy-copy-2">
            We think everyday nourishment should be simpler.
          </motion.p>
          <motion.p {...fade} transition={{ duration: 1, ease: EASE, delay: 0.2 }} className="mt-6 text-lg leading-relaxed text-haze" data-testid="philosophy-copy-3">
            No More Bluff is being built around small, practical choices that fit into real life — not an ideal version of it.
          </motion.p>
        </div>
      </div>

      {/* full-bleed ingredient still-life, softly parallaxed */}
      <motion.div {...fade} className="relative mt-20 -mx-6 overflow-hidden md:mt-32 md:-mx-10" data-testid="philosophy-image-frame">
        <div className="h-[70vh] min-h-[420px] overflow-hidden md:h-[92vh]">
          <motion.img
            src="https://images.unsplash.com/photo-1626697556426-8a55a8af4999?q=85&w=2400&auto=format&fit=crop"
            alt="A row of small bowls, each holding a different nut or seed"
            data-testid="philosophy-image"
            style={{ y: imgY }}
            className="h-full w-full scale-[1.12] object-cover"
            loading="lazy"
          />
        </div>
        <p className="eyebrow mt-5 text-center !text-[0.6rem]">Everyday things. Kept simple.</p>
      </motion.div>
    </section>
  )
}
